import styled from "styled-components";

/* Layout constants shared by the CSS and the measure logic (one source of truth). */
export const CARD_W = 172;
export const GAP = 20;
export const MAX_BRANCH_COLS = 4; /* columns a wide screen is allowed to use */
export const MAX_LEAF_COLS = 4; /* width of a clustered leaf block */
export const MIN_SCALE = 0.6;

/* Scoped styled wrapper — replaces the old global `<style>` injection.
   Class-based selectors are nested so the recursive markup stays simple. */
export const Chart = styled.div`
  --rail: #c3d2df;
  --flow: #7f97b0;
  --line: #d5e0ea;
  --ink: #173453;
  --muted: #7089a0;
  --teal: #3f88a5;
  --cross: #8a6fb0;
  --gap: ${GAP}px;
  --card: ${CARD_W}px;
  width: 100%;
  max-width: 100%;
  background: transparent;
  padding: 4px 0;
  color: var(--ink);
  font-family: "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;

  * { box-sizing: border-box; }

  .ocx-bar { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; margin-bottom: 16px; }
  .ocx-tally { margin-left: auto; font-size: 12px; color: var(--muted); white-space: nowrap; }

  .ocx-stage { position: relative; width: 100%; overflow: hidden; }
  .ocx-fit { position: absolute; top: 0; left: 0; transform-origin: top left; }
  .ocx-tree { position: relative; width: max-content; padding-bottom: 4px; }

  .ocx-node { display: flex; flex-direction: column; align-items: center; }

  /* dashed "also reports to" edges, measured from the DOM and drawn behind the cards */
  .ocx-xlinks { position: absolute; top: 0; left: 0; z-index: 1; overflow: visible; pointer-events: none; }
  .ocx-xlinks path {
    fill: none; stroke: var(--cross); stroke-width: 1.5; stroke-dasharray: 4 4;
    stroke-linecap: round; stroke-linejoin: round; opacity: 0.75; pointer-events: stroke;
    transition: opacity 0.15s, stroke-width 0.15s;
  }
  .ocx-xlinks path:hover { opacity: 1; stroke-width: 2.4; }
  .ocx-xlinks marker path { fill: var(--cross); stroke: none; opacity: 1; }

  .ocx-card {
    position: relative; z-index: 2; width: var(--card); background: #fff; border: 1.5px solid var(--bd);
    border-radius: 12px; padding: 11px 11px 13px; text-align: center;
    box-shadow: 0 1px 3px rgba(23, 52, 83, 0.07); transition: box-shadow 0.15s, border-color 0.15s;
  }
  .ocx-card:hover { box-shadow: 0 5px 16px rgba(23, 52, 83, 0.14); }
  .ocx-card.is-root { background: #2f4a6b; border-color: #2f4a6b; box-shadow: 0 5px 18px rgba(23, 52, 83, 0.24); }
  .ocx-card::before {
    content: ""; position: absolute; left: 13px; right: 13px; top: -1.5px; height: 3px;
    border-radius: 0 0 3px 3px; background: var(--ac);
  }
  .ocx-card.is-root::before { display: none; }

  .ocx-name {
    font-size: 13px; font-weight: 700; line-height: 1.28; letter-spacing: -0.01em;
    overflow-wrap: anywhere; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;
  }
  .ocx-role { font-size: 11px; color: var(--muted); margin-top: 3px; }
  .is-root .ocx-name { color: #fff; }
  .is-root .ocx-role {
    color: rgba(255, 255, 255, 0.75); font-weight: 600; letter-spacing: 0.08em;
    text-transform: uppercase; font-size: 10px;
  }

  /* collapse / expand control */
  .ocx-pill {
    position: absolute; left: 50%; bottom: -12px; transform: translateX(-50%); z-index: 3;
    min-width: 36px; height: 21px; padding: 0 8px;
    display: inline-flex; align-items: center; justify-content: center; gap: 5px;
    border: 1px solid var(--line); border-radius: 999px; background: #fff; cursor: pointer;
    font: inherit; font-size: 10.5px; font-weight: 700; color: var(--muted);
    box-shadow: 0 2px 5px rgba(23, 52, 83, 0.14); transition: border-color 0.15s, color 0.15s, box-shadow 0.15s;
  }
  .ocx-pill:hover { border-color: var(--teal); color: var(--teal); box-shadow: 0 3px 9px rgba(63, 136, 165, 0.24); }
  .ocx-pill svg { transition: transform 0.18s ease; color: var(--ink); opacity: 0.7; }
  .ocx-pill:hover svg { color: var(--teal); opacity: 1; }
  .ocx-pill[aria-expanded="false"] svg { transform: rotate(-90deg); }

  .ocx-kids { position: relative; padding-top: 26px; display: flex; flex-direction: column; align-items: center; }
  .ocx-kids::before { content: ""; position: absolute; top: 0; left: calc(50% - 1px); width: 2px; height: 26px; background: var(--rail); }
  .ocx-row { position: relative; display: flex; justify-content: center; align-items: flex-start; gap: var(--gap); overflow: hidden; }
  .ocx-link { position: relative; height: 22px; width: 100%; }
  .ocx-link::before { content: ""; position: absolute; top: 0; bottom: 0; left: calc(50% - 1px); width: 2px; background: var(--rail); }

  .ocx-slot { position: relative; padding-top: 26px; display: flex; justify-content: center; overflow: visible; }
  .ocx-slot::before { content: ""; position: absolute; top: 0; left: calc(50% - 1px); width: 2px; height: 26px; background: var(--rail); }
  /* data-flow arrowhead pointing down into each card */
  .ocx-slot::after {
    content: ""; position: absolute; top: 18px; left: calc(50% - 5px); width: 0; height: 0;
    border-left: 5px solid transparent; border-right: 5px solid transparent; border-top: 7px solid var(--flow);
  }
  .ocx-bus-l, .ocx-bus-r { position: absolute; top: 0; height: 2px; background: var(--rail); }
  .ocx-bus-l { right: 50%; left: -9999px; }
  .ocx-bus-r { left: 50%; right: -9999px; }

  /* clustered leaves */
  .ocx-cluster { display: flex; flex-direction: column; align-items: center; }

  /* co-heads: multiple top cards drop into a shared bar, then one trunk descends */
  .ocx-co { display: flex; flex-direction: column; align-items: center; }
  .ocx-co-heads { position: relative; display: flex; justify-content: center; align-items: flex-start; gap: var(--gap); overflow: hidden; }
  .ocx-co-slot { position: relative; padding-bottom: 22px; overflow: visible; }
  .ocx-co-slot::after { content: ""; position: absolute; bottom: 0; left: calc(50% - 1px); width: 2px; height: 22px; background: var(--rail); }
  .ocx-co-busl, .ocx-co-busr { position: absolute; bottom: 0; height: 2px; background: var(--rail); }
  .ocx-co-busl { right: 50%; left: -9999px; }
  .ocx-co-busr { left: 50%; right: -9999px; }
  .ocx-co-trunk { position: relative; width: 2px; height: 24px; background: var(--rail); }
  /* flow arrow at the bottom of the merge trunk */
  .ocx-co-trunk::after {
    content: ""; position: absolute; bottom: -1px; left: calc(50% - 5px); width: 0; height: 0;
    border-left: 5px solid transparent; border-right: 5px solid transparent; border-top: 7px solid var(--flow);
  }

  .ocx-legend {
    display: flex; gap: 16px; flex-wrap: wrap; margin-top: 18px; padding-top: 14px;
    border-top: 1px solid var(--line); font-size: 11.5px; color: var(--muted);
  }
  .ocx-legend span { display: inline-flex; align-items: center; gap: 6px; }
  .ocx-legend i { width: 9px; height: 9px; border-radius: 3px; display: block; }
  .ocx-legend i.ocx-dash {
    width: 18px; height: 0; border-radius: 0; border-top: 2px dashed var(--cross); background: none;
  }

  @media (max-width: 560px) { & { --card: 148px; --gap: 14px; padding: 14px; } }
  @media (prefers-reduced-motion: reduce) { .ocx-card, .ocx-pill svg { transition: none; } }
`;
